const TOKEN_KEY = 'netegate.business.token';
const ACCOUNT_KEY = 'netegate.business.account';

export interface Account {
  id: string;
  email: string;
  businessName: string;
  role: 'venue_admin' | 'business_group_admin';
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getAccount(): Account | null {
  const raw = localStorage.getItem(ACCOUNT_KEY);
  return raw ? (JSON.parse(raw) as Account) : null;
}

export function setSession(token: string, account: Account): void {
  // localStorage rather than sessionStorage, unlike the internal Central
  // console: this is a customer's own account on their own device, and being
  // signed out on every tab close would be a poor experience for a venue admin
  // checking their dashboard from a phone.
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(ACCOUNT_KEY, JSON.stringify(account));
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ACCOUNT_KEY);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const response = await fetch(`/api/v1${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  if (response.status === 401 && token) {
    clearSession();
    window.location.hash = '#/signin';
    throw new ApiError('unauthorized', 'Your session has expired. Please sign in again.', 401);
  }

  const body = response.status === 204 ? null : await response.json().catch(() => null);

  if (!response.ok) {
    const error = body as { code?: string; message?: string; details?: Record<string, unknown> } | null;
    throw new ApiError(
      error?.code ?? 'request_failed',
      error?.message ?? `Something went wrong (${response.status}).`,
      response.status,
      error?.details,
    );
  }

  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),

  async signUp(input: { email: string; businessName: string; password?: string }) {
    const result = await request<{ accessToken: string; account: Account }>('/accounts/signup', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    setSession(result.accessToken, result.account);
    return result.account;
  },

  async signInWithPassword(email: string, password: string) {
    const result = await request<{ accessToken: string; account: Account }>('/accounts/signin/password', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setSession(result.accessToken, result.account);
    return result.account;
  },

  requestCode: (email: string) =>
    request<{ challengeRef: string; expiresAt: string }>('/accounts/signin/code/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  async signInWithCode(challengeRef: string, code: string) {
    const result = await request<{ accessToken: string; account: Account }>('/accounts/signin/code/verify', {
      method: 'POST',
      body: JSON.stringify({ challengeRef, code }),
    });
    setSession(result.accessToken, result.account);
    return result.account;
  },
};

export function money(amount: number | string | null | undefined, currency = 'AED'): string {
  return `${currency} ${Number(amount ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function shortDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

export function relativeTime(value: string | null | undefined): string {
  if (!value) return 'never';
  const seconds = Math.floor((Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`;
  return `${Math.floor(seconds / 86400)} d ago`;
}
